import { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import TopImage from "../../../common/TopImage";
import { Images } from "../../../../../config/images";
import "./License.css";
import "../../Dashboard/Dashboard.css";
import BackButton from "../../../../common/BackButton";
import CustomInput from "../../../../common/CustomInput";
import CustomSelect from "../../../../common/CustomSelect";
import SuccessModal from "../../../../common/SuccessModal";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePayInvoice } from "../../../../../hooks/school/billing/usePayInvoice";
import { useAddOnLicenses } from "../../../../../hooks/licenses/useAddOnLicenses";
import { Button } from "react-bootstrap";
import useGetInvoiceDetailForPayment from "../../../../../hooks/payment/useGetInvoiceDetailForPayment";

/**
 * Loading Skeleton
 */
const PaymentSkeleton = () => (
  <div className="container-fluid py-4">
    <div className="card shadow-sm rounded-3">
      <div className="card-body p-4">
        <div className="placeholder-glow mb-4">
          <span className="placeholder col-4 mb-3"></span>
          <div className="row g-3">
            <div className="col-6">
              <span
                className="placeholder col-12 rounded"
                style={{ height: "60px" }}
              ></span>
            </div>
            <div className="col-6">
              <span
                className="placeholder col-12 rounded"
                style={{ height: "60px" }}
              ></span>
            </div>
          </div>
        </div>
        <hr />
        <div className="placeholder-glow mb-4">
          <span className="placeholder col-5 mb-3"></span>
          <span
            className="placeholder col-12 rounded"
            style={{ height: "100px" }}
          ></span>
        </div>
        <hr />
        <div className="d-grid gap-2 placeholder-glow">
          <span
            className="placeholder col-12 rounded"
            style={{ height: "50px" }}
          ></span>
        </div>
      </div>
    </div>
  </div>
);

/**
 * @typedef {"school" | "organization"} tenanttype
 */

/**
 * ManualPaymentForm component - Transaction details form for manual payment
 * Supports both invoice payment and add-on license requests
 * @param {{ type?: tenanttype }} props
 */
export const ManualPaymentForm = ({ type = "school" }) => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get add-on data from navigation state (if coming from add-on license flow)
  const addOnData = location.state || null;
  const isAddOnFlow = addOnData?.licenseType === "add-on";

  // Hooks for payment
  const {
    payInvoice,
    loading: invoiceLoading,
    error: invoiceError,
  } = usePayInvoice();
  const {
    requestAddOnLicenses,
    loading: addOnLoading,
    error: addOnError,
  } = useAddOnLicenses();

  // Invoice Fetching Hook
  const {
    loading: invoiceFetchLoading,
    error: invoiceFetchError,
    invoiceData,
    getInvoiceDetail,
  } = useGetInvoiceDetailForPayment();

  useEffect(() => {
    if (paymentId && !isAddOnFlow) {
      getInvoiceDetail(paymentId);
    }
  }, [paymentId, isAddOnFlow]);

  const pendingAmount =
    invoiceData?.due_amount !== undefined
      ? parseFloat(invoiceData.due_amount)
      : invoiceData &&
        invoiceData.total_amount !== undefined &&
        invoiceData.paid_amount !== undefined
        ? parseFloat(invoiceData.total_amount) -
        parseFloat(invoiceData.paid_amount)
        : 0;

  const currency = invoiceData?.currency || addOnData?.currency || "INR";

  const [transactionReceipts, setTransactionReceipts] = useState([]);
  const [receiptError, setReceiptError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Combined loading and error states for submission
  const isSubmissionLoading = isAddOnFlow ? addOnLoading : invoiceLoading;
  const submissionError = isAddOnFlow ? addOnError : invoiceError;

  const PAYMENT_MODES = [
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "NEFT", value: "neft" },
    { label: "RTGS", value: "rtgs" },
    { label: "IMPS", value: "imps" },
    { label: "UPI", value: "upi" },
  ];

  // Label width configuration for inline layout
  const labelWidth = {
    mobile: "100%",
    tablet: "180px",
    desktop: "180px",
  };

  // Yup validation schema
  const validationSchema = yup.object().shape({
    transactionId: yup
      .string()
      .required("Transaction ID is required")
      .max(100, "Transaction ID must be at most 100 characters"),
    amountPaid: yup
      .number()
      .typeError("Please enter a valid amount")
      .required("Amount is required")
      .positive("Amount must be greater than 0")
      .test(
        "max-amount",
        `Amount cannot exceed ${pendingAmount.toLocaleString()} ${currency}`,
        function (value) {
          if (isAddOnFlow) return true;
          if (!value) return true;
          // Allow a small epsilon for float comparison if needed, but exact check usually fine for currency if typed
          return value <= pendingAmount;
        },
      ),
    paymentMode: yup.string().required("Payment mode is required"),
    remarks: yup.string().max(500, "Remarks must be at most 500 characters"),
  });

  // Initial form values - pre-fill amount for add-on flow
  const initialValues = {
    transactionId: "",
    amountPaid: isAddOnFlow ? addOnData?.totalAmount || "" : "",
    paymentMode: "",
    remarks: "",
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setTransactionReceipts((prev) => [...prev, ...files]);
    setReceiptError("");
    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setTransactionReceipts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values) => {
    setReceiptError("");

    if (transactionReceipts.length === 0) {
      return setReceiptError("At least one payment receipt image is required");
    }

    try {
      if (isAddOnFlow) {
        // Add-on license flow - use requestAddOnLicenses
        const data = await requestAddOnLicenses({
          addon_licenses: addOnData.addonLicenses,
          price_per_license: addOnData.pricePerLicense,
          amount: values.amountPaid,
          source: "offline",
          method: values.paymentMode,
          reference_id: values.transactionId,
          payment_proofs: transactionReceipts,
          currency: currency,
        });

        if (data && data?.success === true) {
          setShowSuccessModal(true);
        }
      } else {
        // Invoice payment flow - use payInvoice
        await payInvoice({
          paymentId,
          amount: values.amountPaid,
          source: "offline",
          method: values.paymentMode,
          reference_id: values.transactionId,
          payment_proofs: transactionReceipts,
          currency: currency,
        });

        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Payment failed", err);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (isAddOnFlow) {
      // Navigate to licenses page for add-on flow
      navigate(
        type === "school"
          ? "/school-admin/settings/licenses"
          : "/organization/settings/licenses",
      );
    } else {
      // Navigate to payments page for invoice flow
      navigate(
        type === "school"
          ? "/school-admin/settings/payments"
          : "/organization/settings/payments",
      );
    }
  };

  return (
    <>
      <div className="container-fluid p-0">
        <div className="pb-2">
          <BackButton />
        </div>

        <section>
          <div className="container-fluid">
            <TopImage imageSrc={Images.TopImage} text="PURCHASE LICENSES" />
          </div>
        </section>

        <div className="purchase-form-container mt-4">
          {/* Add-on Summary Section */}
          {isAddOnFlow && (
            <div className="price-summary-card mb-4">
              <h4 className="price-summary-title">Add-on License Summary</h4>
              <div className="price-details">
                <div className="price-row">
                  <span className="price-label">Number of Licenses:</span>
                  <span className="price-value">{addOnData.addonLicenses}</span>
                </div>
                <div className="price-row">
                  <span className="price-label">Price per License:</span>
                  <span className="price-value">
                    {addOnData.pricePerLicense?.toLocaleString()} ({currency})
                  </span>
                </div>
                <div className="price-row price-total">
                  <span className="price-label">Total Amount:</span>
                  <span className="price-value total-price">
                    {addOnData.totalAmount?.toLocaleString()} ({currency})
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="purchase-form-section">
            <h3 className="purchase-form-title">Manual Payment Form</h3>

            {!isAddOnFlow && invoiceFetchLoading ? (
              <PaymentSkeleton />
            ) : !isAddOnFlow && invoiceFetchError ? (
              <div className="alert alert-danger">{invoiceFetchError}</div>
            ) : (
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isSubmitting }) => (
                  <Form className="d-flex flex-column gap-3">
                    <CustomInput
                      label="Transaction ID"
                      name="transactionId"
                      type="text"
                      placeholder="Enter transactionId"
                      layout="inline"
                      labelWidth={labelWidth}
                      showAstreisk={false}
                    />

                    <CustomInput
                      label={`Amount Paid (${currency})`}
                      name="amountPaid"
                      type="number"
                      placeholder="Enter amount"
                      layout="inline"
                      labelWidth={labelWidth}
                      showAstreisk={false}
                      readOnly={isAddOnFlow}
                    />
                    {!isAddOnFlow && (
                      <div style={{ marginLeft: labelWidth.desktop }}>
                        <small className="text-muted d-block mb-2">
                          You can pay any amount up to{" "}
                          <strong>
                            {pendingAmount.toLocaleString()} ({currency})
                          </strong>
                        </small>
                      </div>
                    )}

                    <CustomSelect
                      label="Payment Mode"
                      name="paymentMode"
                      layout="inline"
                      labelWidth={labelWidth}
                      showAstreisk={false}
                    >
                      <option value="">Select Payment Mode</option>
                      {PAYMENT_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </CustomSelect>

                    <CustomInput
                      label="Remarks"
                      name="remarks"
                      type="text"
                      placeholder="Paid via SBI"
                      layout="inline"
                      labelWidth={labelWidth}
                      showAstreisk={false}
                    />

                    <div className="manual-payment-row">
                      <label className="manual-payment-label">
                        Transaction Receipt
                      </label>
                      <label className="file-upload-btn">
                        <i className="bi bi-upload"></i>
                        Upload Image
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                      </label>
                    </div>

                    {/* Image Preview */}
                    {transactionReceipts.length > 0 && (
                      <div className="row mt-2 g-3">
                        {transactionReceipts.map((file, index) => (
                          <div
                            key={index}
                            className={`${transactionReceipts.length === 1
                              ? "col-12 col-md-6"
                              : transactionReceipts.length === 2
                                ? "col-6 col-md-4"
                                : "col-6 col-md-3"
                              }`}
                          >
                            <div className="position-relative border rounded p-1">
                              <img
                                src={URL.createObjectURL(file)}
                                alt="receipt"
                                className="img-fluid rounded"
                                style={{
                                  height: "120px",
                                  objectFit: "cover",
                                  width: "100%",
                                }}
                                onLoad={(e) =>
                                  URL.revokeObjectURL(e.target.src)
                                }
                              />
                              <br />

                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                onClick={() => handleRemoveImage(index)}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ERRORS */}
                    {receiptError && (
                      <div className="custom-error">{receiptError}</div>
                    )}

                    {submissionError && (
                      <div className="custom-error">{submissionError}</div>
                    )}

                    <div className="mt-2">
                      <Button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting || isSubmissionLoading}
                      >
                        {isSubmissionLoading ? "Submitting..." : "Submit"}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>

      <SuccessModal
        showModal={showSuccessModal}
        handleClose={handleSuccessModalClose}
        title="Success"
        message={
          isAddOnFlow
            ? "Your add-on license request has been submitted successfully. Our team will verify your payment and activate the licenses."
            : "You have successfully added the payment receipt. Our team will verify and update your payment status."
        }
        primaryButtonText="Continue"
        onPrimaryClick={handleSuccessModalClose}
      />
    </>
  );
};

export default ManualPaymentForm;
